import React from "react";
import {
  BaggageClaim,
  Backpack,
  Laptop,
  Package,
  Shirt,
  Plane,
  ShoppingBag,
  Briefcase,
  GraduationCap,
  Baby,
  Umbrella,
  Boxes,
} from "lucide-react";

export interface CategoryItem {
  id: number;
  name: string;
  icon: React.ReactNode;
  parentId?: number | null;
  image?: any;
}

/* ──────────────────────────────────────────────
   ICON HELPER — keeps icon size consistent
   ────────────────────────────────────────────── */
const sz = 18; // default icon size (px)

/* ──────────────────────────────────────────────
   1️⃣ TOP-LEVEL LUGGAGE CATEGORIES (500000xx)
   ────────────────────────────────────────────── */
export const idLuggageCategories: CategoryItem[] = [
  { id: 50000001, name: "Carry-ons", icon: <BaggageClaim size={sz} /> },
  { id: 50000002, name: "Suitcases", icon: <BaggageClaim size={sz} /> },
  { id: 50000003, name: "Backpacks", icon: <Backpack size={sz} /> },
  { id: 50000004, name: "Laptop Bags", icon: <Laptop size={sz} /> },
  { id: 50000005, name: "Duffels", icon: <Package size={sz} /> },
  { id: 50000006, name: "Garment Bags", icon: <Shirt size={sz} /> },
  { id: 50000007, name: "Messenger Bags", icon: <Briefcase size={sz} /> },
  { id: 50000008, name: "Travel Totes", icon: <ShoppingBag size={sz} /> },
  { id: 50000009, name: "Kids Luggage", icon: <Baby size={sz} /> },
  { id: 50000010, name: "Luggage Sets", icon: <Boxes size={sz} /> },
  { id: 50000011, name: "Umbrellas", icon: <Umbrella size={sz} /> },
  { id: 50000012, name: "Travel Accessories", icon: <Plane size={sz} /> },
];

/* ──────────────────────────────────────────────
   2️⃣ LUGGAGE SUBCATEGORIES (50000xxx)
   ────────────────────────────────────────────── */
export const idLuggageSubcategories: CategoryItem[] = [
  // Carry-ons
  { id: 50000101, parentId: 50000001, name: "Hard Shell Carry-ons", icon: <BaggageClaim size={sz} /> },
  { id: 50000102, parentId: 50000001, name: "Soft Carry-ons", icon: <BaggageClaim size={sz} /> },
  { id: 50000103, parentId: 50000001, name: "Airline Approved", icon: <Plane size={sz} /> },

  // Suitcases
  { id: 50000201, parentId: 50000002, name: "Checked Suitcases", icon: <BaggageClaim size={sz} /> },
  { id: 50000202, parentId: 50000002, name: "Expandable Suitcases", icon: <BaggageClaim size={sz} /> },
  { id: 50000203, parentId: 50000002, name: "Lightweight Suitcases", icon: <BaggageClaim size={sz} /> },

  // Backpacks
  { id: 50000301, parentId: 50000003, name: "Travel Backpacks", icon: <Backpack size={sz} /> },
  { id: 50000302, parentId: 50000003, name: "School Backpacks", icon: <GraduationCap size={sz} /> },
  { id: 50000303, parentId: 50000003, name: "Business Backpacks", icon: <Briefcase size={sz} /> },

  // Laptop Bags
  { id: 50000401, parentId: 50000004, name: "Laptop Sleeves", icon: <Laptop size={sz} /> },
  { id: 50000402, parentId: 50000004, name: "Rolling Laptop Bags", icon: <Laptop size={sz} /> },

  // Duffels
  { id: 50000501, parentId: 50000005, name: "Travel Duffels", icon: <Package size={sz} /> },
  { id: 50000502, parentId: 50000005, name: "Gym Duffels", icon: <Package size={sz} /> },

  // Garment Bags
  { id: 50000601, parentId: 50000006, name: "Hanging Garment Bags", icon: <Shirt size={sz} /> },
  { id: 50000602, parentId: 50000006, name: "Foldable Garment Bags", icon: <Shirt size={sz} /> },

  // Messenger Bags
  { id: 50000701, parentId: 50000007, name: "Work Messenger Bags", icon: <Briefcase size={sz} /> },
  { id: 50000702, parentId: 50000007, name: "Casual Messenger Bags", icon: <Briefcase size={sz} /> },

  // Travel Totes
  { id: 50000801, parentId: 50000008, name: "Weekender Totes", icon: <ShoppingBag size={sz} /> },
  { id: 50000802, parentId: 50000008, name: "Foldable Totes", icon: <ShoppingBag size={sz} /> },

  // Kids Luggage
  { id: 50000901, parentId: 50000009, name: "Kids Suitcases", icon: <Baby size={sz} /> },
  { id: 50000902, parentId: 50000009, name: "Character Backpacks", icon: <Baby size={sz} /> },

  // Luggage Sets
  { id: 50001001, parentId: 50000010, name: "2–3 Piece Sets", icon: <Boxes size={sz} /> },
  { id: 50001002, parentId: 50000010, name: "Family Sets", icon: <Boxes size={sz} /> },

  // Umbrellas
  { id: 50001101, parentId: 50000011, name: "Compact Umbrellas", icon: <Umbrella size={sz} /> },
  { id: 50001102, parentId: 50000011, name: "Windproof Umbrellas", icon: <Umbrella size={sz} /> },

  // Travel Accessories
  { id: 50001201, parentId: 50000012, name: "Packing Cubes", icon: <Package size={sz} /> },
  { id: 50001202, parentId: 50000012, name: "Luggage Locks & Tags", icon: <BaggageClaim size={sz} /> },
  { id: 50001203, parentId: 50000012, name: "Travel Pillows & Comfort", icon: <Plane size={sz} /> },
];

/* ──────────────────────────────────────────────
   3️⃣ LUGGAGE PRODUCT VARIANTS (50010101+)
   ────────────────────────────────────────────── */
export const idLuggageProducts: CategoryItem[] = [
  { id: 50010101, parentId: 50000101, name: "Polycarbonate Carry-ons", icon: <BaggageClaim size={sz} /> },
  { id: 50020101, parentId: 50000201, name: "Large Checked Suitcases", icon: <BaggageClaim size={sz} /> },
  { id: 50030101, parentId: 50000301, name: "40L Travel Backpacks", icon: <Backpack size={sz} /> },
  { id: 50040101, parentId: 50000401, name: "13–15 inch Laptop Sleeves", icon: <Laptop size={sz} /> },
  { id: 500120101, parentId: 50001201, name: "Compression Packing Cubes", icon: <Package size={sz} /> },
];